import PropTypes from 'prop-types';
import { createContext, useCallback, useEffect, useMemo, useRef } from 'react';
import { connect, useSelector } from 'react-redux';
import { Link, Outlet, useLoaderData, useLocation, useMatch, useNavigate } from 'react-router';
import queryString from 'query-string';

import Loading from '../../components/Loading';
import GenericHelmet from '../generic-helmet/GenericHelmet';
import { trackEvent, trackPageView } from '../../support-and-tracking/analytics-actions';
import { getProjectFromId } from '../projects-reducer';
import { getSharingTextForProject, hasRecurringPaymentEnabled, isMinnespleis } from '../project-model';
import {
    fetchProjectCard,
    fetchProjectTransactionAggregates,
    fetchRewards,
    fetchRewardsPurchasedCount,
} from '../project-actions';
import Main from '../../frame/main/Main';
import ConfettiExplosion from 'react-confetti-explosion';
import CMSItem from '../../cms/cms-item/CMSItem';
import {
    checkIfUserHasMadeDecisionOnTax,
    fetchBoughtRewardsForTransaction,
    getTransaction,
} from '../../payment/transaction-actions';
import { fetchCommentForTransaction, fetchPromotedGiverForTransaction } from '../../transaction/transaction-actions';
import { pluralize } from '../../utils';
import { DELETE_CART } from '../../constants';
import withRouter from '../../components/hooks/withRouter';
import { POST_PAYMENT_PATH } from '../../create-data-routes';
import { getLatestRewardsForTransaction } from '../../transaction/transaction-reducer';
import { useProjectTimeline } from '../project-timeline/useProjectTimeline';
import ProjectContext from '../project-context';
import styles from './post-payment.module.less';
import { fetchOrganization } from '../../organization/organization-actions';
import { getOrganizationById } from '../../organization/organizations-reducer';
import OrganizationContext from '../../organization/organization-context';
import { fetchSubprojectsForProjectPublic } from '../subproject/subproject-actions';
import { isRewardCreateParticipant } from '../reward/reward-model';
import { useI18nTexts } from '../i18n/useI18nTexts';
import { BACK_TO_PROJECT_TEXT_KEY, isI18nEnabled, POST_PAYMENT_DESCRIPTION_TEXT_KEY } from '../i18n/project-i18n-model';
import { Heading1 } from '@sb1/ffe-core-react';
import { ClientOnly } from '../../components/ClientOnly';

const ALL_STEPS = [
    'trading-card',
    'lottery',
    'register-participant',
    'giveaway',
    'name',
    'promoted-giver',
    'recurring',
    'giver-info',
    'share',
];
const CMS_USP_CONTENT_ID = '2b11Nxbcguk9Nl8qVpA9Nf';

export const PostPaymentStepDoneContext = createContext();

function shouldShowGiverForm(project) {
    return (
        project?.profile?.giverInfo?.showTaxDeductionForm ||
        project?.profile?.giverInfo?.showEmailGiverInfoForm ||
        project?.profile?.giverInfo?.showPhoneNumberGiverInfoForm
    );
}

const PostPayment = ({ project, transactionId }) => {
    const h1Ref = useRef(null);
    const data = useLoaderData();
    const haveMadeADecisionAboutTax = data?.haveMadeADecisionAboutTax;
    const match = useMatch(`${POST_PAYMENT_PATH}/:step/*`);
    const location = useLocation();
    const navigate = useNavigate();

    const texts = useI18nTexts([POST_PAYMENT_DESCRIPTION_TEXT_KEY, BACK_TO_PROJECT_TEXT_KEY], project);

    const canShowHistory = useProjectTimeline(project);
    const organization = useSelector((state) => getOrganizationById(state, project?.organization_id));

    const transactionForGiverInfo = useSelector((state) => state.transactions.transactionForGiverInfo);
    const rewardsForTransaction = useSelector((state) => getLatestRewardsForTransaction(state, transactionId));
    const rewards = rewardsForTransaction?.rewards;

    const existingComment = useSelector((state) => state.transactions.commentForEdit);

    const defaultPromotedGiverLimit = useSelector((state) => state.promotedGiverLimit);
    const promotedGiverLimit = project?.profile?.promotedGiverLimit ?? defaultPromotedGiverLimit;

    const currentStep = match?.params?.step ?? '';
    const query = queryString.parse(location.search);
    const { recurringPaymentSuccess, initial } = query;

    const currentStepIsLegal = stepIsLegal({
        step: currentStep,
        initial,
        project,
        rewards,
        transactionForGiverInfo,
        existingComment,
        promotedGiverLimit,
        haveMadeADecisionAboutTax,
    });

    const stepToIndexFrom =
        currentStep === 'recurring-vipps' || currentStep === 'profundo-recurring' ? 'recurring' : currentStep;
    const currentStepIndex = ALL_STEPS.indexOf(stepToIndexFrom);
    const nextLegalStep = ALL_STEPS.slice(currentStepIndex + 1).find((step) =>
        stepIsLegal({
            step,
            initial,
            project,
            rewards,
            transactionForGiverInfo,
            existingComment,
            promotedGiverLimit,
            haveMadeADecisionAboutTax,
            isInternational: isI18nEnabled(project),
        })
    );

    const gotProjectCard = !!project?.created_at;
    const gotTransaction = !!transactionForGiverInfo;

    useEffect(() => {
        if (h1Ref.current) {
            h1Ref.current.focus();
        }
    }, []);

    useEffect(() => {
        trackEvent('support', 'confirmation-show', { 'project-id': project?.id });
        trackPageView({
            urlAsEvent: 'project-payment',
            properties: { 'project-id': project?.id },
        });
    }, [project?.id]);

    useEffect(() => {
        if (gotProjectCard && gotTransaction && !currentStepIsLegal) {
            done();
        }
    }, [currentStepIsLegal, done, gotProjectCard, gotTransaction]);

    const backUrl = useMemo(() => {
        if (canShowHistory) {
            return `/project/${project?.id}/history`;
        }

        return `/project/${project?.id}`;
    }, [canShowHistory, project?.id]);

    const backText = texts[BACK_TO_PROJECT_TEXT_KEY];

    const rewardText = useMemo(() => {
        if (rewards?.length > 0) {
            const combinedRewardTitles = rewards?.map((reward) => reward.title).join(', ');
            const rewardText =
                rewards?.length > 0
                    ? `Du har valgt ${pluralize(
                          rewards?.length,
                          'belønningen',
                          'belønningene'
                      )} «${combinedRewardTitles}». `
                    : '';
            return rewardText;
        }
        return '';
    }, [rewards]);

    const isOnShareStep = currentStep === 'share';

    const done = useCallback(() => {
        navigate(`${nextLegalStep}${location.search}`);
    }, [nextLegalStep, location.search, navigate]);

    const recurringDone = useCallback(
        ({ success = false }) => {
            trackEvent('support', 'give-recurring', {
                action: success ? 'success' : 'failed',
                'project-id': project?.id,
            });
            navigate(`${nextLegalStep}${success ? '?recurringPaymentSuccess=true' : ''}`);
        },
        [project?.id, navigate, nextLegalStep]
    );

    const recurringRetry = () => {
        navigate(`recurring`);
    };

    if (!transactionForGiverInfo || !gotProjectCard) {
        return (
            <Main>
                <Loading />
            </Main>
        );
    }

    const thankYouText = project?.profile?.thankYouText ? (
        <>{project.profile.thankYouText}</>
    ) : (
        <>{`Takk for at du ${recurringPaymentSuccess ? 'ble fast giver!' : 'støttet spleisen!'} 🙌`}</>
    );

    return (
        <Main>
            <GenericHelmet
                title={project.title}
                description={getSharingTextForProject(project)}
                path={location.pathname}
                imageUrl={project.img_url}
                projectId={project.id}
            >
                <div className={styles.container}>
                    {!isMinnespleis(project) && (
                        <ClientOnly>
                            <ConfettiExplosion duration={5000} particleCount={120} className={styles.confettiCanvas} />
                        </ClientOnly>
                    )}
                    {!isOnShareStep && (
                        <div className={styles.thanksContainer}>
                            <Heading1 tabIndex={-1} ref={h1Ref} className={styles.header}>
                                {thankYouText}
                            </Heading1>
                            <div className={styles.subHeader}>
                                {rewardText}
                                {recurringPaymentSuccess
                                    ? 'Fremtidige trekk kan når som helst avsluttes fra Vipps-appen'
                                    : texts[POST_PAYMENT_DESCRIPTION_TEXT_KEY]}
                            </div>
                        </div>
                    )}
                    <PostPaymentStepDoneContext.Provider value={{ done, recurringDone, recurringRetry }}>
                        <ProjectContext.Provider value={project}>
                            <OrganizationContext.Provider
                                value={{ organization, themeColor: organization?.profile?.themeColor }}
                            >
                                <Outlet context={{ isOnShareStep }} />
                            </OrganizationContext.Provider>
                        </ProjectContext.Provider>
                    </PostPaymentStepDoneContext.Provider>
                    <div>
                        <Link className={styles.backLink} to={backUrl}>
                            {backText}
                        </Link>
                    </div>
                    <CMSItem id={CMS_USP_CONTENT_ID} />
                </div>
            </GenericHelmet>
        </Main>
    );
};

export const loader = async ({ dispatch, params }) => {
    const { projectId, transactionId, transactionNonce } = params;
    const project = (await dispatch(fetchProjectCard(projectId)))?.data;
    if (!project?.id) {
        throw new Response('Project not found', { status: 404 });
    }
    const promises = [
        dispatch(fetchProjectTransactionAggregates(projectId)),
        dispatch(fetchSubprojectsForProjectPublic(projectId)),
    ];
    if (transactionId) {
        promises.push(
            dispatch(fetchCommentForTransaction({ transactionId })),
            dispatch(fetchPromotedGiverForTransaction({ transactionId })),
            dispatch(fetchRewards(projectId, true)),
            dispatch(fetchRewardsPurchasedCount(projectId))
        );
        if (transactionNonce) {
            promises.push(dispatch(fetchBoughtRewardsForTransaction(transactionId, transactionNonce)));
            promises.push(dispatch(getTransaction(transactionId, transactionNonce)));
        }
        promises.push(
            dispatch({
                type: DELETE_CART,
                data: { projectId },
            })
        );
    }
    if (project?.organization_id) {
        promises.push(dispatch(fetchOrganization(project.organization_id)));
    }
    await Promise.all(promises);

    if (shouldShowGiverForm(project)) {
        const response = await dispatch(checkIfUserHasMadeDecisionOnTax({ transactionId }));
        return { haveMadeADecisionAboutTax: response?.madeDecisionOnTax };
    }

    return {};
};

const hasRewardsLotteryTicket = (rewards) => {
    return rewards?.filter((reward) => !!reward.lottery_ticket).length > 0;
};

const stepIsLegal = ({
    step,
    initial,
    project,
    rewards,
    transactionForGiverInfo,
    existingComment,
    promotedGiverLimit,
    haveMadeADecisionAboutTax,
    isInternational,
}) => {
    const showRegisterParticipantForm = rewards?.some(isRewardCreateParticipant) && project?.subprojects_enabled;

    const hasGivenNameOrCommentOrCommentImage =
        !!transactionForGiverInfo?.from_user_meta_data?.name ||
        !!existingComment?.comment ||
        !!existingComment?.img_url;
    const isAnonymousGiver = !!transactionForGiverInfo?.from_user_meta_data?.anonymous;
    const madeDecisionAboutNameOrCommentEarlier =
        !!initial && (hasGivenNameOrCommentOrCommentImage || isAnonymousGiver);

    const showPromotedGiverForm =
        !project?.profile?.promotedGiverDisabled && transactionForGiverInfo?.amount >= promotedGiverLimit;

    const showGiverInfoForm = shouldShowGiverForm(project);

    switch (step) {
        case '':
            return false;
        case 'giveaway':
            return project?.profile?.giveaway?.enabled && !isInternational;
        case 'register-participant':
            return showRegisterParticipantForm && !isInternational;
        case 'lottery':
            return hasRewardsLotteryTicket(rewards) && !isInternational;
        case 'name':
            return (
                !project?.profile?.anonymousGiver &&
                !project?.profile?.tradingCardEnabled &&
                !madeDecisionAboutNameOrCommentEarlier
            );
        // Not translated to english
        case 'promoted-giver':
            return showPromotedGiverForm;
        case 'giver-info':
            return !!showGiverInfoForm && !haveMadeADecisionAboutTax && !isInternational;
        case 'recurring':
        case 'recurring-vipps':
        case 'profundo-recurring':
            return hasRecurringPaymentEnabled({ project }) && transactionForGiverInfo?.isVipps && !isInternational;
        case 'share':
            return true;
        case 'trading-card':
            return project?.profile?.tradingCardEnabled;
        default:
            return true;
    }
};

PostPayment.propTypes = {
    dispatch: PropTypes.func.isRequired,
    project: PropTypes.object.isRequired,
    baseURL: PropTypes.string.isRequired,
};

function mapStateToProps(state, props) {
    const projectId = parseInt(props.params.projectId, 10);
    const project = getProjectFromId(state, projectId);

    return {
        transactionId: props.params.transactionId,
        transactionNonce: props.params.transactionNonce,
        baseURL: state.baseURL,
        project,
    };
}

export default withRouter(connect(mapStateToProps)(PostPayment));
